import React, { useEffect, useMemo, useState } from 'react';

import { Home } from '@mui/icons-material';
import { Spinner } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { usePagination, useTable } from 'react-table';

import { listAllDmsDrivers } from './operations';
import { clientLabel, compareDrivers, downloadCsv, driverLabel, isDriverActive } from './utils';
import Goto from '../../assets/goto.png';
import { Breadcrumbs, ExportCsvButton, Footer, Modal, SubHeader, TablePagination } from '../../components';

import './styles.scoped.css';

const BENEFITS_ROLE_NAME = 'Driver Log Benefits';

const DriverList = ({ variant }) => {
  const isActiveList = variant === 'active';
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [showReportStubModal, setShowReportStubModal] = useState(false);

  const selectedRoleId = useSelector(state => state.dashboard.selectedRoleId);
  const allApps = useSelector(state => state.dashboard.unfliteredAppsList);
  const isBenefitsRole = useMemo(
    () =>
      (allApps || [])
        .flatMap(app => app.roles?.items ?? [])
        .some(role => role.id === selectedRoleId && role.name === BENEFITS_ROLE_NAME),
    [allApps, selectedRoleId],
  );

  useEffect(() => {
    listAllDmsDrivers()
      .then(response => setDrivers(response))
      .catch(e => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return drivers
      .filter(driver => !isActiveList || isDriverActive(driver))
      .filter(driver => !search || driverLabel(driver).toLowerCase().includes(search))
      .sort(compareDrivers);
  }, [drivers, isActiveList, searchText]);

  const columns = useMemo(
    () => [
      { Header: 'Driver Name & Number', accessor: driverLabel, id: 'driver' },
      { Header: 'LOB', accessor: 'lob' },
      {
        Header: 'Current Client Name & Number',
        accessor: row => clientLabel(row.currentClientName, row.currentClientId),
        id: 'client',
      },
      {
        Header: 'Service Action',
        accessor: 'serviceAction',
        className: 'centered',
        Cell: ({ value }) => (value ? <span className="action-badge service" title="Service Action" /> : null),
      },
      {
        Header: 'Benefits Action',
        accessor: 'benefitsAction',
        className: 'centered',
        Cell: ({ value }) => (value ? <span className="action-badge benefits" title="Benefits Action" /> : null),
      },
      {
        Header: '',
        id: 'goto',
        Cell: ({ row }) => (
          <button
            type="button"
            className="icon-button"
            aria-label={`Go to ${driverLabel(row.original)}`}
            onClick={() => navigate(`/dms-driver-log/driver/${row.original.id}`)}>
            <img className="goto-icon" src={Goto} alt="" />
          </button>
        ),
      },
    ],
    [navigate],
  );

  const tableInstance = useTable({ columns, data: rows, initialState: { pageSize: 25 } }, usePagination);
  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } = tableInstance;

  const handleExport = () =>
    downloadCsv(
      `dms-${variant}-drivers.csv`,
      ['Driver Name & Number', 'LOB', 'Current Client Name & Number'],
      rows.map(driver => [
        driverLabel(driver),
        driver.lob,
        clientLabel(driver.currentClientName, driver.currentClientId),
      ]),
    );

  const subHeaderButtons = [
    ...(isActiveList && isBenefitsRole
      ? [{ text: 'GENERATE PROVIDER REPORT', onPress: () => setShowReportStubModal(true) }]
      : []),
    { icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') },
  ];

  return (
    <>
      <SubHeader text="DMS Driver Log" buttons={subHeaderButtons} />
      <Breadcrumbs
        leftTitle="DMS Driver Log"
        rightTitle={isActiveList ? 'Active Drivers' : 'All Drivers'}
        route="/dms-driver-log"
      />
      <div className="wrap">
        <div className="export-row">
          <ExportCsvButton onClick={handleExport} />
        </div>
        <div className="list-controls">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              className="search-input"
              aria-label="Search by driver name or candidate ID"
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
            />
          </div>
          <TablePagination tableInstance={tableInstance} />
        </div>
        {loadError && <div className="field-error">{loadError}</div>}
        {loading ? (
          <Spinner animation="border" style={{ margin: '40px auto', color: 'var(--primary)' }} />
        ) : (
          <table className="driver-table" {...getTableProps()}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th {...column.getHeaderProps({ className: column.className })}>{column.render('Header')}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <td {...cell.getCellProps({ className: cell.column.className })}>{cell.render('Cell')}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <Modal
        title="Generate Provider Report"
        body="The provider report will be available once the B-Swift integration is live."
        isOpen={showReportStubModal}
        primaryButtonTitle="OK"
        onClick={() => setShowReportStubModal(false)}
      />
      <Footer />
    </>
  );
};

export default DriverList;
